import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    Box, Button, Card, Container, Typography, Grid, MenuItem, Select, Divider, IconButton,
    LinearProgress, Chip, Paper, Alert, Stack, Tabs, Tab
} from '@mui/material';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, LineElement, CategoryScale, LinearScale, PointElement, Tooltip, Legend } from 'chart.js';
import RefreshIcon from '@mui/icons-material/Refresh';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SchoolIcon from '@mui/icons-material/School';
import AssessmentIcon from '@mui/icons-material/Assessment';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {getRequest} from "../../Fetch/request";
import Loader from '../Loader/Loader';
import DSA_TOPICS from '../../Utils/DSATopics';

// Register Chart.js components
ChartJS.register(LineElement, CategoryScale, LinearScale, PointElement, Tooltip, Legend);

// Generate topic list from DSA_TOPICS keys
const topicsDSA = Object.keys(DSA_TOPICS);
const topicsCore = ['Operating_Systems', 'Database_Systems', 'Computer_Networks'];

const ImproveWeakAreas = () => {

    const location = useLocation();
    const [isLoading, setIsLoading] = useState(false);

    const sampleTopicData = {
        "totalAttempt": "-",
        "totalCorrect": "-",
        "totalWrong": "-",
        "masteryRating": "-",
        "correctSubmissionCountLastFourWeeks": [0, 0, 0, 0],
        "wrongSubmissionCountLastFourWeeks": [0, 0, 0, 0],
        "referenceMaterial": "",
        "totalNoOfQuestions": "-",
        "difficultyLevel": "-",
        "estimatedQuestionsNeeded": "-",
        "threshold": "-"
    };

    const navigate = useNavigate();
    const [topicData, setTopicData] = useState(sampleTopicData);
    const [selectedTopic, setSelectedTopic] = useState("ARRAY");
    const [activeTab, setActiveTab] = useState(0);

    const topicList = topicsDSA;

    // Calculate accuracy percentage
    const accuracyPercentage = (topicData.totalAttempt !== "-" && topicData.totalAttempt > 0) 
        ? (topicData.totalCorrect / topicData.totalAttempt) * 100 
        : "-";
    
    // Calculate progress towards mastery
    const masteryProgress = (topicData.masteryRating !== "-") 
        ? (topicData.masteryRating / 2400) * 100 
        : "-";

    const performanceGraphData = {
        labels: ['Week 1', 'Week 2', 'Week 3', 'Week 4'],
        datasets: [
            {
                label: 'Correct Answers',
                data: topicData.correctSubmissionCountLastFourWeeks,
                borderColor: '#4CAF50',
                backgroundColor: 'rgba(76, 175, 80, 0.1)',
                tension: 0.4,
                fill: true,
            },
            {
                label: 'Wrong Answers',
                data: topicData.wrongSubmissionCountLastFourWeeks,
                borderColor: '#F44336',
                backgroundColor: 'rgba(244, 67, 54, 0.1)',
                tension: 0.4,
                fill: true,
                borderWidth: 2,
            },
        ],
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top',
                align: 'center',
                labels: {
                    usePointStyle: true,
                    padding: 20,
                    font: {
                        size: 12
                    }
                }
            },
            tooltip: {
                mode: 'index',
                intersect: false,
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                grid: {
                    color: 'rgba(0,0,0,0.1)',
                },
                ticks: {
                    stepSize: 1,
                    callback: function(value) {
                        return Math.floor(value);
                    }
                },
                title: {
                    display: true,
                    text: 'Number of Questions',
                    font: {
                        size: 12,
                        weight: 'bold'
                    }
                }
            },
            x: {
                display: true,
                grid: {
                    display: true,
                    color: 'rgba(0,0,0,0.1)',
                },
                ticks: {
                    font: {
                        size: 11
                    }
                },
                title: {
                    display: true,
                    text: 'Weeks',
                    font: {
                        size: 12,
                        weight: 'bold'
                    }
                }
            },
        },
        layout: {
            padding: {
                top: 20,
                bottom: 20
            }
        }
    };

    useEffect(() => {
        if( location !== null && location.state !== null && location.state.topic !== null){
            setSelectedTopic(location.state.topic);
        }
    }, []);

    // Only fetch data when explicitly requested via refresh button
    // Removed automatic fetch on topic change

    const refreshPage = () => {
        const fetchData = async () => {
            setIsLoading(true);
            try {
                const profile = JSON.parse(localStorage.getItem("profileObject"));
                const userId = profile?.id || localStorage.getItem('userId') || 'slazysloth_user';

                // Use the mapped topic value for API call
                const topicValue = DSA_TOPICS[selectedTopic];
                const url = `${process.env.REACT_APP_URL}/topics?userId=${userId}&topicId=${topicValue}&type=DSA`;

                const getResponse = await getRequest(url);
                if (getResponse?.status === "200" && getResponse.response != null ) {
                    setTopicData(getResponse.response);
                }
            } catch (error) {
                console.error("Error fetching data:", error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    };

    const getDifficultyColor = (level) => {
        switch(level) {
            case 1: return '#4CAF50';
            case 2: return '#FF9800';
            case 3: return '#F44336';
            default: return '#9E9E9E';
        }
    };

    const getDifficultyText = (level) => {
        switch(level) {
            case 1: return 'Easy';
            case 2: return 'Medium';
            case 3: return 'Hard';
            default: return 'Unknown';
        }
    };

    // Helper function to format topic display name
    const formatTopicDisplayName = (topic) => {
        return topic.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
    };

    // Mock data for top mistakes and points to remember
    const topMistakes = [
        "Forgetting to handle edge cases in array operations",
        "Incorrect time complexity analysis",
        "Not considering space complexity",
        "Missing base cases in recursive solutions",
        "Incorrect implementation of two-pointer technique"
    ];

    const pointsToRemember = [
        "Always check for null/empty input before processing",
        "Consider both time and space complexity",
        "Use appropriate data structures for the problem",
        "Test with edge cases and boundary conditions",
        "Practice pattern recognition for common problems"
    ];

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    return (
        <Container
            maxWidth="lg"
            sx={{
                marginTop: '30px',
                marginBottom: '30px',
                padding: '20px',
            }}
        >


            {/* Topic Selection */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    <Select
                        value={selectedTopic}
                        onChange={(e) => setSelectedTopic(e.target.value)}
                    sx={{ 
                        backgroundColor: '#fff', 
                        borderRadius: '8px',
                        minWidth: 200
                    }}
                    >
                        {topicList.map((topic, index) => (
                            <MenuItem key={index} value={topic}>
                            {formatTopicDisplayName(topic)}
                            </MenuItem>
                        ))}
                    </Select>
                    <IconButton
                        onClick={refreshPage}
                        sx={{
                        backgroundColor: '#3498db',
                            color: '#fff',
                        width: 48,
                        height: 48,
                        borderRadius: '50%',
                        '&:hover': { 
                            backgroundColor: '#2980b9',
                            transform: 'scale(1.1)'
                        },
                        transition: 'all 0.2s ease'
                        }}
                    >
                        <RefreshIcon />
                    </IconButton>

            </Box>

            {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                    <Loader />
                </Box>
            ) : (
                <>
                                        {/* Key Metrics Cards */}
                    <Grid container spacing={2} sx={{ mb: 3, justifyContent: 'center' }}>
                        <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex' }}>
                            <Card sx={{ 
                                p: 2, 
                                borderRadius: '8px', 
                                textAlign: 'center', 
                                bgcolor: '#f5f5f5', 
                                border: '1px solid #ddd',
                                width: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                minHeight: '120px'
                            }}>
                                <Typography variant="h4" sx={{ color: '#333', fontWeight: 'bold', mb: 0.5, lineHeight: 1 }}>
                                    {accuracyPercentage === "-" ? "-" : `${accuracyPercentage.toFixed(1)}%`}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#333', fontWeight: 'bold', mb: 0.5 }}>
                                    Accuracy Rate
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#666', lineHeight: 1.2 }}>
                                    {topicData.totalCorrect} / {topicData.totalAttempt} correct
                                </Typography>
                            </Card>
                            </Grid>
                        <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex' }}>
                            <Card sx={{ 
                                p: 2, 
                                borderRadius: '8px', 
                                textAlign: 'center', 
                                bgcolor: '#f5f5f5', 
                                border: '1px solid #ddd',
                                width: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                minHeight: '120px'
                            }}>
                                <Typography variant="h4" sx={{ color: '#333', fontWeight: 'bold', mb: 0.5, lineHeight: 1 }}>
                                    {topicData.totalCorrect}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#333', fontWeight: 'bold', mb: 0.5 }}>
                                    Questions Correct
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#666', lineHeight: 1.2 }}>
                                    Out of {topicData.totalNoOfQuestions} total
                                </Typography>
                            </Card>
                            </Grid>
                        <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex' }}>
                            <Card sx={{ 
                                p: 2, 
                                borderRadius: '8px', 
                                textAlign: 'center', 
                                bgcolor: '#f5f5f5', 
                                border: '1px solid #ddd',
                                width: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                minHeight: '120px'
                            }}>
                                <Typography variant="h4" sx={{ color: '#333', fontWeight: 'bold', mb: 0.5, lineHeight: 1 }}>
                                    {topicData.totalWrong}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#333', fontWeight: 'bold', mb: 0.5 }}>
                                    Questions Wrong
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#666', lineHeight: 1.2 }}>
                                    Need improvement
                                </Typography>
                            </Card>
                        </Grid>
                    </Grid>

                    {/* Analysis Section with Tabs */}
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Card sx={{ p: 3, borderRadius: '12px' }}>
                                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                                    <Tabs 
                                        value={activeTab} 
                                        onChange={handleTabChange}
                                        sx={{
                                            '& .MuiTab-root': {
                                                textTransform: 'none',
                                                fontWeight: 'bold',
                                                fontSize: '14px',
                                                minWidth: 'auto',
                                                padding: '12px 24px'
                                            },
                                            '& .Mui-selected': {
                                                color: '#008080'
                                            },
                                            '& .MuiTabs-indicator': {
                                                backgroundColor: '#008080'
                                            }
                                        }}
                                    >
                                        <Tab label="Learning Resources" />
                                        <Tab label="Top Mistakes" />
                                        <Tab label="Points to Remember" />
                                    </Tabs>
                                </Box>

                                {/* Tab Content */}
                                {activeTab === 0 && (
                                    <Box sx={{ 
                                        height: '220px',
                                        overflowY: 'auto',
                                        pt: 0, pb: 2, pr: 2, pl: 0
                                    }}>
                                        {/* Learning Resources List */}
                                        <Box sx={{ mb: 2 }}>
                                            <Typography 
                                                variant="body2" 
                                                component="a"
                                                onClick={() => {
                                                    const topicValue = DSA_TOPICS[selectedTopic];
                                                    navigate(`/problem-details/${encodeURIComponent(topicValue)}`);
                                                }}
                                                sx={{ 
                                                    color: '#008080',
                                                    textDecoration: 'none',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    cursor: 'pointer',
                                                    fontWeight: '500',
                                                    fontSize: '16px',
                                                    '&:hover': {
                                                        textDecoration: 'none',
                                                        color: '#006666'
                                                    }
                                                }}
                                            >
                                                <OpenInNewIcon sx={{ mr: 1, fontSize: '18px' }} />
                                                Practice Questions
                                            </Typography>
                                        </Box>
                                        
                                        {/* Placeholder for future links */}
                                        <Box sx={{ mb: 2 }}>
                                            <Typography 
                                                variant="body2" 
                                                sx={{ 
                                                    color: '#999',
                                                    fontSize: '12px',
                                                    fontStyle: 'italic'
                                                }}
                                            >
                                                More resources coming soon...
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}

                                {activeTab === 1 && (
                                    <Box sx={{ 
                                        height: '220px',
                                        overflowY: 'auto',
                                        pt: 0, pb: 2, pr: 2, pl: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        <Box sx={{ textAlign: 'center' }}>
                                            <Typography variant="h6" sx={{ color: '#d32f2f', fontWeight: 'bold', mb: 2 }}>
                                                Top Mistakes Analysis
                                            </Typography>
                                            <Typography variant="body1" sx={{ color: '#666', fontStyle: 'italic' }}>
                                                Analysis is coming soon...
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: '#999', mt: 1 }}>
                                                In the meantime, please attempt some problems.
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: '#999', mt: 1 }}>
                                                You will be notified when analysis is ready.
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}

                                {activeTab === 2 && (
                                    <Box sx={{ 
                                        height: '220px',
                                        overflowY: 'auto',
                                        pt: 0, pb: 2, pr: 2, pl: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        <Box sx={{ textAlign: 'center' }}>
                                            <Typography variant="h6" sx={{ color: '#2e7d32', fontWeight: 'bold', mb: 2 }}>
                                                Points to Remember Analysis
                                            </Typography>
                                            <Typography variant="body1" sx={{ color: '#666', fontStyle: 'italic' }}>
                                                Analysis is coming soon...
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: '#999', mt: 1 }}>
                                                In the meantime, please attempt some problems.
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: '#999', mt: 1 }}>
                                                You will be notified when analysis is ready.
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}
                            </Card>
                        </Grid>
                    </Grid>


                </>
            )}


        </Container>
    );
};

export default ImproveWeakAreas;
